/**
 * ============================================================
 * KEJJA OS — src/hooks/usePush.ts
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function usePushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function usePushStatus() {
  return useQuery({
    queryKey: ['push', 'status'],
    queryFn: async () => {
      if (!usePushSupported()) return { supported: false, subscribed: false, permission: 'default' as NotificationPermission };
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      return {
        supported: true,
        subscribed: Boolean(sub),
        permission: Notification.permission,
      };
    },
  });
}

export function useEnablePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { publicKey } = await api.get<{ publicKey: string }>(`/notifications/vapid-public-key`);
      if (!publicKey) throw new Error('Push not configured on server');

      if (Notification.permission === 'denied') {
        throw new Error('Notifications blocked — enable them in browser settings');
      }

      const perm = await Notification.requestPermission();
      if (perm !== 'granted') throw new Error('Permission denied');

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      }

      const json = sub.toJSON();
      await api.post(`/notifications/push-subscribe`, {
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
        userAgent: navigator.userAgent,
      });

      return { subscribed: true };
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['push'] }); },
  });
}

export function useDisablePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post(`/notifications/push-unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      return { subscribed: false };
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['push'] }); },
  });
}

export function useSendTestPush() {
  return useMutation({
    mutationFn: () => api.post<{ sent: number }>(`/notifications/push-test`, {}),
  });
}
